import React, { useEffect, useState } from "react";
import {
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  useTheme,
  Box,
  TableContainer,
  Chip,
} from "@mui/material";
import VantageListHead from "../../../component/ListHead/SignalList";
import { hashInvestment, hashInvestmentReverse, applySortFilter, getComparator, normalizeTickerForComparison } from "../../../component/Methods";

const DifferenceSection = ({
  currentSignals,
  modifiedSignalsData,
}) => {

  const theme = useTheme();
  const [differenceData, setDifferenceData] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("value");

  // Create table headers for difference section
  const TABLE_HEAD_DIFFERENCE = [
    { id: "name", label: "Ticker", alignCenter: true },
    { id: "value", label: "Weight", alignCenter: true },
    { id: "long_short", label: "L/S", alignCenter: true },
    { id: "assetType", label: "Asset Type", alignCenter: true },
    { id: "change", label: "Change", alignCenter: true },
  ];

  useEffect(() => {
    // Create maps for easier lookup
    const currentMap = new Map();
    const modifiedMap = new Map();

    // Use the utility function to normalize ticker for comparison
    // This treats '.' and '/' as equivalent (e.g., TSLA.A = TSLA/A)
    const normalizeTicker = normalizeTickerForComparison;

    // Helper function to normalize long_short value
    const normalizeLongShort = (value, existingLongShort) => {
      // If existing long_short is available, use it; otherwise derive from value
      if (existingLongShort) {
        return existingLongShort.toUpperCase();
      }
      return parseFloat(value) >= 0 ? "L" : "S";
    };

    // Build current signals map
    currentSignals.forEach(signal => {
      const ticker = normalizeTicker(signal.name);
      if (!ticker) return; // Skip if no valid ticker
      
      // Normalize assetType - prefer existing assetType, fallback to hashInvestment
      let assetType = signal.assetType;
      if (!assetType && signal.investment) {
        assetType = hashInvestment(signal.investment);
      }
      
      currentMap.set(ticker, {
        ...signal,
        ticker,
        name: signal.name,
        value: parseFloat(signal.value) || 0,
        long_short: signal.long_short || normalizeLongShort(signal.value),
        assetType: assetType || "",
      });
    });

    // Build modified signals map
    // When there are duplicates (e.g., ABC.B and ABC/B), prioritize the one that represents a change
    // Strategy: If a duplicate normalized ticker exists, use the one that's different from current
    // This handles the case where user adds ABC.B (90) but ABC/B (80) already exists in modifiedSignalsData
    modifiedSignalsData.forEach(signal => {
      const ticker = normalizeTicker(signal.name);
      if (!ticker) return; // Skip if no valid ticker
      
      // Normalize assetType - prefer existing assetType, fallback to hashInvestment
      let assetType = signal.assetType;
      if (!assetType && signal.investment !== undefined) {
        assetType = hashInvestment(signal.investment) || "Equity";
      }
      if (!assetType) assetType = "Equity";
      
      const parsedValue = parseFloat(signal.value) || 0;
      const createSignalObject = () => ({
        ...signal,
        ticker,
        name: signal.name.toUpperCase(),
        value: parsedValue,
        long_short: normalizeLongShort(signal.value, signal.long_short),
        assetType: assetType,
      });
      
      // If this ticker already exists in modifiedMap, check which one to keep
      if (modifiedMap.has(ticker)) {
        const existingSignal = modifiedMap.get(ticker);
        const existingValue = parseFloat(existingSignal.value) || 0;
        const currentSignal = currentMap.get(ticker);
        const currentValue = currentSignal ? (parseFloat(currentSignal.value) || 0) : null;
        
        // If current exists, prefer the value that's different from current (the modified/new one)
        if (currentValue !== null) {
          const existingDiffers = Math.abs(existingValue - currentValue) > 0.0001;
          const newDiffers = Math.abs(parsedValue - currentValue) > 0.0001;
          
          // Priority: Use the one that differs from current (represents a modification)
          // If both differ or both match, use new (last occurrence)
          if (newDiffers || (!existingDiffers && !newDiffers)) {
            modifiedMap.set(ticker, createSignalObject());
          }
          // Otherwise keep existing (existing differs, new matches - keep the one that differs)
        } else {
          // No current signal, just use the last one (newer)
          modifiedMap.set(ticker, createSignalObject());
        }
      } else {
        // First occurrence of this normalized ticker
        modifiedMap.set(ticker, createSignalObject());
      }
    });

    // Find all unique tickers from both sets
    const allTickers = new Set([...currentMap.keys(), ...modifiedMap.keys()]);

    // Build difference data - show ALL signals
    const differences = [];

    allTickers.forEach(ticker => {
      const currentSignal = currentMap.get(ticker);
      const modifiedSignal = modifiedMap.get(ticker);

      let changeType = "UNCHANGED";
      let displayData = null;
      let weightDifference = 0;

      if (modifiedSignal && Math.abs(modifiedSignal.value || 0) < 0.0001) {
        // Modified signal exists but weight is 0 - treat as CLOSED
        changeType = "CLOSED";
        displayData = { ...modifiedSignal };
      } else if (currentSignal && modifiedSignal) {
        // Ticker exists in both - check if values changed
        const currentValue = currentSignal.value || 0;
        const modifiedValue = modifiedSignal.value || 0;
        const currentAssetType = (currentSignal.assetType || "Equity").trim().toUpperCase();
        const modifiedAssetType = (modifiedSignal.assetType || "Equity").trim().toUpperCase();
        const currentLongShort = (currentSignal.long_short || "").toUpperCase();
        const modifiedLongShort = (modifiedSignal.long_short || "").toUpperCase();

        // Check if any key attributes changed
        const valueChanged = Math.abs(currentValue - modifiedValue) > 0.0001; // Small tolerance for floating point
        const assetTypeChanged = currentAssetType !== modifiedAssetType;
        const longShortChanged = currentLongShort !== modifiedLongShort;

        if (valueChanged || assetTypeChanged || longShortChanged) {
          // Determine specific change type based on position direction and weight change
          const currentIsLong = currentValue >= 0;
          const modifiedIsLong = modifiedValue >= 0;
          const weightIncreased = modifiedValue > currentValue;

          if (currentIsLong && modifiedIsLong) {
            // Both positions are long
            changeType = weightIncreased ? "CHANGED - BL" : "CHANGED - SL";
          } else if (!currentIsLong && !modifiedIsLong) {
            // Both positions are short
            changeType = weightIncreased ? "CHANGED - CS" : "CHANGED - SS";
          } else {
            // Position direction changed (long to short or short to long)
            changeType = "CHANGED";
          }

          weightDifference = modifiedValue - currentValue;
        }

        displayData = { ...modifiedSignal }; // Show modified data for changed items
      } else if (!currentSignal && modifiedSignal) {
        // Only in modified signals
        changeType = "OPEN";
        displayData = { ...modifiedSignal };
      } else if (currentSignal && !modifiedSignal) {
        // Only in current signals
        changeType = "CLOSED";
        displayData = { ...currentSignal };
      }

      if (displayData) {
        differences.push({
          ...displayData,
          change: changeType,
          weightDifference,
        });
      }
    });

    // Apply sorting to the differences data
    const sortedDifferences = applySortFilter(differences, getComparator(order, orderBy));

    setDifferenceData(sortedDifferences);
  }, [currentSignals, modifiedSignalsData, order, orderBy]);

  const getChangeChipColor = (changeType) => {
    switch (changeType) {
      case "OPEN":
        return "success";
      case "CLOSED":
        return "error";
      case "CHANGED":
      case "CHANGED - BL":
        return "success";
      case "CHANGED - SL":
        return "warning";
      case "CHANGED - CS":
        return "error";
      case "CHANGED - SS":
        return "info";
      case "UNCHANGED":
        return "default";
      default:
        return "default";
    }
  };

  // Only show rows when there are modified signals
  const shouldShowRows = modifiedSignalsData.length > 0;

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  return (
    <Box
      sx={{
        borderRadius: "4px",
        marginTop: "10px",
        width: "100%",
        marginRight: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableContainer
        sx={{
          flex: 1,
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <Table
          size="small"
          stickyHeader
          aria-label="difference table"
        >
          <VantageListHead
            order={order}
            orderBy={orderBy}
            headLabel={TABLE_HEAD_DIFFERENCE}
            onRequestSort={handleRequestSort}
            noStatus={true}
            customizeFont={12}
          />
          <TableBody>
            {!shouldShowRows ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{
                    fontSize: "12px",
                    color: theme.palette.text.secondary,
                    py: 3,
                  }}
                >
                  Add signals to Modified Signals section to see differences
                </TableCell>
              </TableRow>
            ) : differenceData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{
                    fontSize: "12px",
                    color: theme.palette.text.secondary,
                    py: 3,
                  }}
                >
                  No signals to compare
                </TableCell>
              </TableRow>
            ) : (
              differenceData.map((row, index) => (
                <TableRow
                  key={`${row.ticker}-${index}`}
                  sx={{
                    overflow: "auto",
                    "&:hover": {
                      background: theme.palette.grey[200],
                      cursor: "pointer",
                    },
                    "& > .MuiTableCell-root": {
                      paddingTop: "1px",
                      paddingBottom: "0px !important",
                      lineHeight: "1.2",
                    },
                  }}
                >
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      color: theme.palette.text.primary,
                      borderRight: "none",
                      cursor: "pointer",
                    }}
                  >
                    {row.name}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      color: theme.palette.text.primary,
                      borderLeft: "none",
                      borderRight: "none",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme.palette.text.primary,
                        textAlign: "right"
                      }}
                    >
                      {row.change === "CLOSED" ? "0%" : `${row.value}%`}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "12px",
                      paddingLeft: "10px",
                      color: theme.palette.text.primary,
                      borderLeft: "none",
                      borderRight: "none",
                      height: "21.5px",
                    }}
                  >
                    {row.long_short}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "12px",
                      paddingLeft: "10px",
                      color: theme.palette.text.primary,
                      borderLeft: "none",
                      borderRight: "none",
                      height: "21.5px",
                    }}
                  >
                    {row.assetType}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      color: theme.palette.text.primary,
                      borderLeft: "none",
                      borderRight: "none",
                    }}
                  >
                    <Chip
                      label={row.change}
                      size="small"
                      color={getChangeChipColor(row.change)}
                      variant="outlined"
                      sx={{
                        fontSize: "10px",
                        height: "15px",
                        minWidth: "50px",
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DifferenceSection;
